// src/app/modules/user/components/patient/PatientBillingReport.tsx
import React, {useState, useMemo, useCallback, useEffect} from 'react'
import {useParams} from 'react-router-dom'
import {saveAs} from 'file-saver'
import * as XLSX from 'xlsx'
import {toast} from 'react-toastify'
import axios from 'axios'
import {BillingDataModel} from '../../../../entities/entities'
import {PATIENT_BILLING_LIST_HEADERS} from '../../../../constants/RoleConstants'
import {USER_ROLES} from '../../../../constants/StatusConstants'

import DataTableList from '../DataTableList'
import ConfirmationModal from '../modals/ConfirmationModal'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {useReduxAuth} from '../../../auth'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'

const getDownloadFilename = (contentDisposition: string | undefined, row: any) => {
  if (contentDisposition) {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
    const encodedFilename = utf8Match?.[1]

    if (encodedFilename) {
      try {
        return decodeURIComponent(encodedFilename)
      } catch {
        return encodedFilename
      }
    }

    if (filenameMatch?.[1]) return filenameMatch[1]
  }

  const start = row?.cycle_start || 'cycle-start'
  const end = row?.cycle_end || 'cycle-end'
  return `Patient_Detailed_Report_${start}_${end}.pdf`
}

const PatientBillingReport: React.FC = () => {
  const {patientId} = useParams<{patientId: string}>()
  const {currentUser} = useReduxAuth()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined

  // State for managing all billing data (not filtered by month)
  const [allBillingData, setAllBillingData] = useState<any>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [pendingRow, setPendingRow] = useState<BillingDataModel | null>(null)
  const [statusNotes, setStatusNotes] = useState<string>('')
  const [downloadingDetailedReportId, setDownloadingDetailedReportId] = useState<
    number | string | null
  >(null)

  // Fetch all billing data for the patient (not limited to a specific month)
  const fetchAllBillingData = useCallback(async () => {
    if (!patientId) return
    
    setLoading(true)
    setError(null)
    try {
      // Build query params - include doctor_id for MA
      const doctorIdParam = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? `doctor_id=${maSelectedDoctorId}`
        : null
      // Fetch all billing data for this patient without month restriction
      const res = await axios.get(API_ENDPOINTS.GET_BILLING_REPORT_BY_PATIENT(patientId, doctorIdParam))

      // Transform raw data using the mapper function
      const patientBillingData = res.data.data.billingData ?? []
      setAllBillingData(patientBillingData)
    } catch (err: any) {
      setError(err.response?.data?.message ?? err.message ?? 'Failed to fetch billing data')
    } finally {
      setLoading(false)
    }
  }, [patientId, roleId, maSelectedDoctorId])

  // Fetch data on component mount
  useEffect(() => {
    if (patientId) {
      fetchAllBillingData()
    }
  }, [patientId, fetchAllBillingData])

  // Toggle status function
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleStatus = useCallback(
    async (row: BillingDataModel & {notes?: string}) => {
      const newStatus = row.Patient.status === 1 ? 0 : 1
      await axios.put(API_ENDPOINTS.UPDATE_PATIENT_STATUS(row.patient_id, newStatus), {
        notes: row.notes,
      })
      // Refresh data after status change
      fetchAllBillingData()
    },
    [fetchAllBillingData]
  )
  // Export to Excel - now using all billing data
  const exportData = useMemo(
    () =>
      allBillingData.map((row: any) =>
        PATIENT_BILLING_LIST_HEADERS.filter((col) => col.exportable !== false).reduce(
          (acc, col) => {
            acc[col.name] = typeof col.selector === 'function' ? col.selector(row) : row[col.key]
            return acc
          },
          {} as Record<string, any>
        )
      ),
    [allBillingData]
  )

  const downloadDetailedReport = useCallback(
    async (row: BillingDataModel) => {
      if (!patientId || row.billing_cycle_id === undefined || row.billing_cycle_id === null) {
        toast.error('Detailed report is not available for this billing cycle.')
        return
      }

      setDownloadingDetailedReportId(row.billing_cycle_id)

      try {
        const response = await axios.get(
          API_ENDPOINTS.GET_PATIENT_DETAILED_BILLING_REPORT(
            patientId,
            row.billing_cycle_id
          ),
          {responseType: 'blob'}
        )
        const filename = getDownloadFilename(response.headers['content-disposition'], row)

        saveAs(response.data, filename)
      } catch {
        toast.error('Detailed report download failed.')
      } finally {
        setDownloadingDetailedReportId(null)
      }
    },
    [patientId]
  )

  const exportToExcel = useCallback(() => {
    try {
      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Billing Report')
      const buf = XLSX.write(wb, {bookType: 'xlsx', type: 'array'})
      saveAs(
        new Blob([buf], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'Patient_Billing_Report.xlsx'
      )
      toast.success('Exported!')
    } catch {
      toast.error('Export failed')
    }
  }, [exportData])

  return (
    <>
      <ConfirmationModal
        isOpen={confirmModalOpen}
        title='Confirm Status Change'
        message="Please provide a reason for changing this patient's status."
        confirmButtonTitle='Yes, Change Status'
        requireNotes={true}
        notes={statusNotes}
        onNotesChange={setStatusNotes}
        onCancel={() => {
          setConfirmModalOpen(false)
          setPendingRow(null)
        }}
        onConfirm={async () => {
          if (!pendingRow) return
          setConfirmModalOpen(false)
          try {
            const nextStatus = pendingRow.status === 1 ? 0 : 1
            await axios.put(
              API_ENDPOINTS.UPDATE_PATIENT_STATUS(pendingRow.Patient.id, nextStatus),
              {
                notes: statusNotes,
              }
            )
            toast.success('Status updated')
            fetchAllBillingData() // Refresh all data
          } catch {
            toast.error('Update failed')
          } finally {
            setPendingRow(null)
            setStatusNotes('')
          }
        }}
      />

      {/* <FilterComponent
        startDate={moment(monthYear, 'MMMM YYYY')}
        endDate={moment(monthYear, 'MMMM YYYY').endOf('month')}
        viewType={viewType}
        showDurationButton={false}
        onFilterChange={onFilterChange}
      /> */}

      {error && <div className='text-red-600'>{error}</div>}
      <DataTableList
        title='Patient Billing Report'
        columns={PATIENT_BILLING_LIST_HEADERS}
        data={allBillingData}
        isLoading={loading}
        showAddButton={false}
        showExportButton={allBillingData.length > 0 && !loading}
        onExportClick={exportToExcel}
        onDetailedReportDownload={downloadDetailedReport}
        downloadingDetailedReportId={downloadingDetailedReportId}
        handleStatusToggle={(row) => {
          // Open our modal instead of toggling directly
          setPendingRow(row)
          setStatusNotes('')
          setConfirmModalOpen(true)
        }}
        allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE]}
      />
    </>
  )
}

export default PatientBillingReport
